import type { TypedDocumentNode } from "@apollo/client";
import {
  ActOnAgentSessionDocument,
  type ActOnAgentSessionMutation,
  type ActOnAgentSessionMutationVariables,
} from "@repo/graphql/documents/agent/mutation.generated";
import {
  AgentMessagesDocument,
  AgentSessionDocument,
  type AgentMessagesQuery,
  type AgentMessagesQueryVariables,
  type AgentSessionQuery,
  type AgentSessionQueryVariables,
} from "@repo/graphql/documents/agent/query.generated";
import {
  GetBrandDocument,
  type GetBrandQuery,
  type GetBrandQueryVariables,
} from "@repo/graphql/documents/brand/query.generated";
import {
  GetCampaignDraftDocument,
  type GetCampaignDraftQuery,
  type GetCampaignDraftQueryVariables,
} from "@repo/graphql/documents/campaignDraft/query.generated";
import {
  PostTypesDocument,
  type PostTypesQuery,
  type PostTypesQueryVariables,
} from "@repo/graphql/documents/generic/query.generated";
import {
  GuestActOnAgentSessionDocument,
  GuestCreateProductDocument,
  type GuestActOnAgentSessionMutation,
  type GuestCreateProductMutation,
} from "@repo/graphql/documents/guest/mutation.generated";
import {
  GuestAgentMessagesDocument,
  GuestAgentSessionDocument,
  GuestBrandDocument,
  GuestCampaignDraftDocument,
  GuestPostTypesDocument,
  GuestProductSelectionDocument,
  GuestProductsSelectionDocument,
  type GuestAgentMessagesQuery,
  type GuestAgentSessionQuery,
  type GuestBrandQuery,
  type GuestCampaignDraftQuery,
  type GuestPostTypesQuery,
  type GuestProductSelectionQuery,
  type GuestProductsSelectionQuery,
} from "@repo/graphql/documents/guest/query.generated";
import {
  CreateProductDocument,
  type CreateProductMutation,
  type CreateProductMutationVariables,
} from "@repo/graphql/documents/product/mutation.generated";
import {
  GetProductSelectionDocument,
  ProductsSelectionDocument,
  type GetProductSelectionQuery,
  type GetProductSelectionQueryVariables,
  type ProductsSelectionQuery,
  type ProductsSelectionQueryVariables,
} from "@repo/graphql/documents/product/query.generated";

/**
 * An operation of the chat, answered by the logged-in document or by its guest twin:
 * the same variables and, through aliases (`agent: guest { get: agentSession(...) }`),
 * the same shape. A component reads the union, so it only compiles against what both
 * answer (the guest's nullable `product` included).
 */
type ChatDocument<Member, Guest, Variables> = TypedDocumentNode<
  Member | Guest,
  Variables
>;

export type ChatDocuments = {
  agentSession: ChatDocument<
    AgentSessionQuery,
    GuestAgentSessionQuery,
    AgentSessionQueryVariables
  >;
  agentMessages: ChatDocument<
    AgentMessagesQuery,
    GuestAgentMessagesQuery,
    AgentMessagesQueryVariables
  >;
  actOnSession: ChatDocument<
    ActOnAgentSessionMutation,
    GuestActOnAgentSessionMutation,
    ActOnAgentSessionMutationVariables
  >;
  brand: ChatDocument<GetBrandQuery, GuestBrandQuery, GetBrandQueryVariables>;
  campaignDraft: ChatDocument<
    GetCampaignDraftQuery,
    GuestCampaignDraftQuery,
    GetCampaignDraftQueryVariables
  >;
  productsSelection: ChatDocument<
    ProductsSelectionQuery,
    GuestProductsSelectionQuery,
    ProductsSelectionQueryVariables
  >;
  productSelection: ChatDocument<
    GetProductSelectionQuery,
    GuestProductSelectionQuery,
    GetProductSelectionQueryVariables
  >;
  createProduct: ChatDocument<
    CreateProductMutation,
    GuestCreateProductMutation,
    CreateProductMutationVariables
  >;
  postTypes: ChatDocument<
    PostTypesQuery,
    GuestPostTypesQuery,
    PostTypesQueryVariables
  >;
};

/** What the campaign chat reads and writes for a logged-in account. */
export const memberChatDocuments: ChatDocuments = {
  agentSession: AgentSessionDocument,
  agentMessages: AgentMessagesDocument,
  actOnSession: ActOnAgentSessionDocument,
  brand: GetBrandDocument,
  campaignDraft: GetCampaignDraftDocument,
  productsSelection: ProductsSelectionDocument,
  productSelection: GetProductSelectionDocument,
  createProduct: CreateProductDocument,
  postTypes: PostTypesDocument,
};

/** The same operations for a browser with no account (the `guest` namespace). */
export const guestChatDocuments: ChatDocuments = {
  agentSession: GuestAgentSessionDocument,
  agentMessages: GuestAgentMessagesDocument,
  actOnSession: GuestActOnAgentSessionDocument,
  brand: GuestBrandDocument,
  campaignDraft: GuestCampaignDraftDocument,
  productsSelection: GuestProductsSelectionDocument,
  productSelection: GuestProductSelectionDocument,
  createProduct: GuestCreateProductDocument,
  postTypes: GuestPostTypesDocument,
};

export const chatDocuments = (guest: boolean): ChatDocuments =>
  guest ? guestChatDocuments : memberChatDocuments;
